import type { NotizKurz } from './daten'

/**
 * **Kleine reine Hilfen der Notizbuch-Ansicht** – ohne React, damit sie sich testen lassen.
 */

/** Eine Notiz mit dem Notizbuch, aus dem sie kommt – die Leiste kombiniert mehrere. */
export interface NotizImBuch extends NotizKurz {
  nb: string
}

/** ISO-Kalenderwoche (Montag beginnt, KW 1 enthält den ersten Donnerstag). */
export function kalenderwoche(d: Date): { jahr: number; woche: number } {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const wochentag = t.getUTCDay() || 7
  t.setUTCDate(t.getUTCDate() + 4 - wochentag)
  const jahresanfang = new Date(Date.UTC(t.getUTCFullYear(), 0, 1))
  const woche = Math.ceil(((t.getTime() - jahresanfang.getTime()) / 86_400_000 + 1) / 7)
  return { jahr: t.getUTCFullYear(), woche }
}

export function gleicherTag(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

const TAG = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })
const UHR = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' })
const LANG = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })

/** „14:05" heute, „Mo., 12.10." dieses Jahr, sonst „12.10.2021". */
export function datumKurz(iso: string, jetzt = new Date()): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  if (gleicherTag(d, jetzt)) return UHR.format(d)
  if (d.getFullYear() === jetzt.getFullYear()) return TAG.format(d)
  return LANG.format(d)
}

/**
 * Der Titel, wenn keiner eingegeben wurde: die erste nichtleere Zeile ohne Markdown-Zeichen.
 * So kann man einfach losschreiben (Z-23, „Das Blatt").
 */
export function titelAusText(text: string): string {
  for (const zeile of text.split('\n')) {
    const sauber = zeile
      .replace(/^\s*(#{1,6}\s+|[-*+]\s+(\[[ xX]\]\s+)?|>\s*|\d+\.\s+)/, '')
      .replace(/[*_`~]/g, '')
      .trim()
    if (sauber) return sauber.slice(0, 120)
  }
  return ''
}

/** Groß/klein egal: „mül" findet „Müller". */
function normal(s: string): string {
  return s.toLocaleLowerCase('de-DE')
}

/**
 * **Stichwortsuche im Speicher** – jedes Wort muss in Titel, Auszug, Tags oder dem vorkommen, was
 * die KI gefunden hat (Kurzfassung, Schlagworte, Personen). Der
 * Bestand liegt ohnehin komplett im Client (Leitplanke 2); die Volltextsuche über den ganzen Text
 * und die Bedeutungssuche kommen mit Stufe 3.
 */
export function suchen<T extends NotizKurz>(notizen: readonly T[], frage: string): T[] {
  const woerter = normal(frage).split(/\s+/).filter(Boolean)
  if (woerter.length === 0) return [...notizen]
  return notizen.filter((n) => {
    const ki = n.ki
      ? `${n.ki.kurzfassung} ${n.ki.schlagworte.join(' ')} ${n.ki.personen.join(' ')}`
      : ''
    const heuhaufen = normal(`${n.titel} ${n.auszug} ${n.tags.join(' ')} ${ki}`)
    return woerter.every((w) => heuhaufen.includes(w))
  })
}

/** Nach Kalenderwoche gruppiert, neueste zuerst – die Chronik. */
export function nachWochen<T extends NotizKurz>(
  notizen: readonly T[],
): { schluessel: string; titel: string; notizen: T[] }[] {
  const gruppen = new Map<string, { schluessel: string; titel: string; notizen: T[] }>()
  const sortiert = [...notizen].sort((a, b) => b.datum.localeCompare(a.datum))
  for (const n of sortiert) {
    const { jahr, woche } = kalenderwoche(new Date(n.datum))
    const schluessel = `${jahr}-${String(woche).padStart(2, '0')}`
    let g = gruppen.get(schluessel)
    if (!g) {
      g = { schluessel, titel: `KW ${woche} · ${jahr}`, notizen: [] }
      gruppen.set(schluessel, g)
    }
    g.notizen.push(n)
  }
  return [...gruppen.values()]
}

/**
 * **Ist das dieselbe Aufgabe?** Die KI formuliert Kästchen leicht um („… — bis 2026-10-29" fällt weg,
 * Satzzeichen ändern sich). Verglichen werden die ersten 40 Zeichen ohne Frist, Satzzeichen und
 * Groß/klein – oder ob der eine Text im anderen steckt.
 */
export function gleicheAufgabe(a: string, b: string): boolean {
  const n = (s: string) =>
    s
      .replace(/\s+[—–-]\s+bis\s+\S+.*$/i, '')
      .replace(/\[[^\]]*\]/g, '')
      .toLocaleLowerCase('de-DE')
      .replace(/[^\p{L}\d]+/gu, ' ')
      .trim()
  const x = n(a)
  const y = n(b)
  if (!x || !y) return false
  return x.slice(0, 40) === y.slice(0, 40) || x.includes(y) || y.includes(x)
}
